import type { LookupEntry } from '@/types/bundle'

// Client-only row identity - lookup_table.json's `entries` is a plain array
// with no id field (unlike document_types' keyed record), so React and the
// single-expanded-row/error-routing state need a stable key that never goes
// to the backend.
export function createLocalKey(): string {
  return crypto.randomUUID()
}

export function createBlankEntry(): LookupEntry {
  return { type: 'estate_agent', name: '', aliases: [], domain: undefined }
}

// Duplicate pre-fills every field from the source except `name`, which
// becomes "Copy of <name>" rather than the exact source string (Frontend/
// Design PRD 05) - not blocked from saving unchanged, just an obvious signal
// it needs editing before the server's own duplicate check would reject it
// anyway.
export function duplicateEntry(source: LookupEntry): LookupEntry {
  const cloned = structuredClone(source)
  cloned.name = `Copy of ${source.name}`
  return cloned
}

// Advisory, client-side-only shape check (schema doesn't constrain `domain`
// beyond `type: string`, and doesn't require it) - not a re-implementation
// of any server-side rule, just a plain "looks like a domain" pattern.
const DOMAIN_SHAPE_RE = /^(?!-)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/

export function isValidDomainShape(domain: string): boolean {
  return DOMAIN_SHAPE_RE.test(domain)
}

// Parses PRD 22's collision-error path (`entries[type=X,name=Y]`,
// gui_backend/core/validation.py's `_lookup_table_collision_errors`) back
// into a {type, name} pair so it can be matched against a specific row -
// never re-derives the collision itself, only reads the server's own answer.
export function parseCollisionPath(path: string): { type: string; name: string } | undefined {
  const match = /^entries\[type=([^,\]]*),name=([^\]]*)\]$/.exec(path)
  if (!match) return undefined
  return { type: match[1], name: match[2] }
}
